import { Again } from '@beyond-js/ui/react';
import { ApiProblem, type Api } from '@cdn/api';
import { Observable } from '@cdn/state';

interface Specs {
	api: Api;
	/** Checks the session once more; it reports through `failed()` when the service did not answer */
	check: () => Promise<unknown>;
	/** Milliseconds between the tries; `@beyond-js/ui`'s `Again` schedule unless a test shortens it */
	delays?: readonly number[];
	clock?: () => number;
}

/**
 * A session check the service did not answer: unavailable, never a sign-out. It is tried again by itself on
 * `@beyond-js/ui`'s `Again` schedule (`again`, decision D132): after 5, 15, 30 and 60 s and then every minute, at
 * once when the browser is back online or the tab is shown again, and on Try again (`check()`), while the outage
 * stays in view. A service that answers but does not allow this page's address (`refused`, sites audit WEB-01)
 * does not change by waiting: nothing is tried.
 */
export class Outage extends Observable {
	readonly again: Again;

	#api: Api;
	#check: () => Promise<unknown>;
	// Whether the check that runs met the outage again
	#failing = false;

	#checking = false;
	/** Whether a check runs */
	get checking() {
		return this.#checking;
	}

	#problem: unknown;
	/** What the last check met, which names who did not answer */
	get problem() {
		return this.#problem;
	}

	#refused = false;
	/** The service answers, but does not allow this page's address */
	get refused() {
		return this.#refused;
	}

	constructor({ api, check, delays, clock }: Specs) {
		super();
		this.#api = api;
		this.#check = check;
		this.again = new Again({ run: () => this.#attempt(), ...(delays ? { steps: delays } : {}), ...(clock ? { now: clock } : {}) });
		this.again.listen(() => this.changed());
	}

	/**
	 * The check met an outage. When no answer arrived, whether the service answers this address at all decides
	 * whether waiting helps; an answer (the service, or the authority behind it, unavailable) is never that.
	 */
	async failed(problem: unknown) {
		this.#failing = true;
		this.#problem = problem;
		const unanswered = ApiProblem.of(problem).status === 0;
		this.#refused = unanswered && typeof this.#api.answers === 'function' && (await this.#api.answers());
		this.#refused ? this.again.stop() : this.again.start();
		this.changed();
	}

	/** Checks again at once (Try again), keeping the outage in view while it does: one check at a time */
	async check() {
		if (this.again.active) await this.again.now();
		else await this.#run();
	}

	/** The session was checked, or the page ends: nothing more is tried */
	stop() {
		this.again.stop();
	}

	// One try of the schedule: answered unless the check met the outage again
	async #attempt(): Promise<boolean> {
		this.#failing = false;
		await this.#run();
		return !this.#failing;
	}

	async #run() {
		if (this.#checking) return;
		this.#checking = true;
		this.changed();
		try {
			await this.#check();
		} finally {
			this.#checking = false;
			this.changed();
		}
	}
}
